/**
 * The action type dispatched when `switchTenant` (this folder's authSlice.js)
 * resolves, as a literal string rather than a reference to the action
 * creator.
 *
 * Any slice that reacts to a tenant switch (metricsSlice, partitionTypesSlice)
 * and the root-level reset wrapper (src/store/resetOnTenantSwitch.js) match
 * on this string instead of importing `switchTenant` from authSlice.js.
 * Importing the action creator there pulls in authSlice.js's own module-scope
 * imports (api/auth, api/tenants -> api/client -> the app's singleton
 * `@/store`) as a side effect. That's harmless during the app's own bootstrap
 * (main.jsx always loads `@/store` first, so the cycle resolves cleanly), but
 * it becomes a real crash the moment something else - a test that imports
 * metricsSlice.js directly, for instance - reaches this cycle from a
 * different starting point: store/index.js ends up re-entering
 * authSlice.js/metricsSlice.js while they're still mid-evaluation, and
 * `authReducer`/`metricsReducer` resolve to `undefined`, which blows up
 * `combineReducers`' reducer-shape check ("baseReducer is not a function").
 * This file has no imports of its own, so referencing it never creates that
 * cycle.
 *
 * Kept in sync with `createAsyncThunk("auth/switchTenant", ...)` by Redux
 * Toolkit's own "/fulfilled" suffix convention; only a deliberate rename of
 * that thunk's type prefix could make this drift.
 */
export const TENANT_SWITCH_FULFILLED = 'auth/switchTenant/fulfilled'

/** The root reducer uses this after an active tenant ID changes locally. */
export const TENANT_CONTEXT_CHANGED = 'auth/tenantContextChanged'

/** Local selections and session refreshes can also change the tenant. */
export function hasTenantContextChanged(previousState, nextState) {
  return String(previousState?.auth?.currentTenantId ?? '')
    !== String(nextState?.auth?.currentTenantId ?? '')
}
